import { DynamoNode, IDimensions, IBorderRadius, IPadding, IStyle, Color } from "dynamo-models";
import React from "react";
import { IEditorState } from "../..";
import { connect } from "react-redux";
import { doSetAttribute } from "../../redux";
import Slider from "@material-ui/core/Slider";
import { Grid, Typography, Input, Paper, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Button, IconButton } from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft';
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter';
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight';
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify';
import ClearIcon from '@material-ui/icons/Clear';
import ButtonGroup from '@material-ui/core/ButtonGroup';

const getValueOrDefault = (val: any | undefined, defaultVal) => val === undefined || val === null ? defaultVal : val;

interface IClassCommands {
    set: (node: DynamoNode, name?: string) => void;

    remove: (node: DynamoNode) => void;
}

interface IDimensionCommands {
    set: (node: DynamoNode, size?: number) => void;
    setWidth: (node: DynamoNode, width?: number) => void;
    setHeight: (node: DynamoNode, height?: number) => void;

    remove: (node: DynamoNode) => void;
    removeWidth: (node: DynamoNode) => void;
    removeHeight: (node: DynamoNode) => void;
}

interface IBorderRadiusCommands {
    set: (node: DynamoNode, radius?: number) => void;
    setTopLeft: (node: DynamoNode, radius?: number) => void;
    setTopRight: (node: DynamoNode, radius?: number) => void;
    setBottomRight: (node: DynamoNode, radius?: number) => void;
    setBottomLeft: (node: DynamoNode, radius?: number) => void;

    remove: (node: DynamoNode) => void;
    removeTopLeft: (node: DynamoNode) => void;
    removeTopRight: (node: DynamoNode) => void;
    removeBottomRight: (node: DynamoNode) => void;
    removeBottomLeft: (node: DynamoNode) => void;
}

interface IBorderWidthCommands {
    set: (node: DynamoNode, width?: number) => void;
    setLeft: (node: DynamoNode, width?: number) => void;
    setTop: (node: DynamoNode, width?: number) => void;
    setRight: (node: DynamoNode, width?: number) => void;
    setBottom: (node: DynamoNode, width?: number) => void;

    remove: (node: DynamoNode) => void;
    removeLeft: (node: DynamoNode) => void;
    removeTop: (node: DynamoNode) => void;
    removeRight: (node: DynamoNode) => void;
    removeBottom: (node: DynamoNode) => void;
}

interface IColorCommands {
    set: (node: DynamoNode, value?: number) => void;
    setRed: (node: DynamoNode, value?: number) => void;
    setGreen: (node: DynamoNode, value?: number) => void;
    setBlue: (node: DynamoNode, value?: number) => void;

    remove: (node: DynamoNode) => void;
}

interface IBorderCommands {
    widthCommands: IBorderWidthCommands;
    radiusCommands: IBorderRadiusCommands;
    colorCommands: IColorCommands;
}

interface IPaddingCommands {
    set: (node: DynamoNode, padding?: number) => void;
    setLeft: (node: DynamoNode, padding?: number) => void;
    setTop: (node: DynamoNode, padding?: number) => void;
    setRight: (node: DynamoNode, padding?: number) => void;
    setBottom: (node: DynamoNode, padding?: number) => void;

    remove: (node: DynamoNode) => void;
    removeLeft: (node: DynamoNode) => void;
    removeTop: (node: DynamoNode) => void;
    removeRight: (node: DynamoNode) => void;
    removeBottom: (node: DynamoNode) => void;
}

interface IAlignmentCommands {
    setHorizontal: (node: DynamoNode, align?: string) => void;

    remove: (node: DynamoNode) => void;
}

interface IStylingCommandsProps {
    dimensionCommands: IDimensionCommands;
    borderCommands: IBorderCommands;
    paddingCommands: IPaddingCommands;
    alignmentCommands: IAlignmentCommands;
    backgroundColorCommands: IColorCommands;
    classNameCommands: IClassCommands;
}

interface IStylingProps extends IStylingCommandsProps {
    node?: DynamoNode;
    isDisabled: boolean;
    maxHeight?: number;
}

interface IStylingState {
}

interface INumericFieldTuple {
    label: string;
    value: number;

    setValue: (value: number) => void;
    removeValue: () => void;
}

const createNumericFieldTuple = (label: string, value: number, setValue: (value: number) => void, removeValue?: () => void): INumericFieldTuple => ({
    label,
    value,
    setValue,
    removeValue: removeValue !== undefined ? removeValue : () => {},
});

interface ITextFieldTuple {
    label: string;
    value?: string;

    setValue: (value?: string) => void;
    removeValue: () => void;
}

const createTextFieldTuple = (label: string, value: string | undefined, setValue: (value?: string) => void, removeValue: () => void): ITextFieldTuple => ({
    label,
    value,
    setValue,
    removeValue,
});

interface IFieldProps {
    isDisabled: boolean;
    canClear: boolean;
}

interface INumericProps extends IFieldProps {
    minValue: number;
    maxValue: number;
    sliderIncrement?: number;
    fieldIncrement?: number;
}

interface INumericFieldProps extends INumericFieldTuple, INumericProps {
}

interface ITextFieldProps extends ITextFieldTuple, IFieldProps {
}

interface INumericFieldSetProps extends INumericProps {
    fieldSet: INumericFieldTuple[];
    setValue: (value: number) => void;
    removeValue: () => void;
    header: string;
}

interface INumericFieldSetState {
    value: number;
    isOpen: boolean;
}

interface IAlignmentFieldProps {
    alignment?: string;
    setValue: (value: string) => void;
    removeValue: () => void;
}

const AlignmentField = (props: IAlignmentFieldProps) => (
    <ButtonGroup size="small" color="primary" style={{ display: "flex", justifyContent: "center", margin: "1em" }}>
        <Button variant="contained" onClick={_ => props.setValue("left")} disabled={props.alignment === "left"}>
            <FormatAlignLeftIcon />
        </Button>
        <Button variant="contained" onClick={_ => props.setValue("center")} disabled={props.alignment === "center"}>
            <FormatAlignCenterIcon />
        </Button>
        <Button variant="contained" onClick={_ => props.setValue("right")} disabled={props.alignment === "right"}>
            <FormatAlignRightIcon />
        </Button>
        <Button variant="contained" onClick={props.removeValue} disabled={props.alignment === undefined}>
            <FormatAlignJustifyIcon />
        </Button>
    </ButtonGroup>
);

const NumericField = (props: INumericFieldProps) => (
    <div>
        <Typography style={{ fontSize: 14, padding: "0.5em"}}>
            {props.label}
        </Typography>
        <div style={{ display: "flex" }}>
            <Slider
                value={props.value}
                onChange={(_, v) => props.setValue(v as number)}
                min={props.minValue}
                max={props.maxValue}
                step={props.sliderIncrement}
                disabled={props.isDisabled}
                style={{ alignSelf: "center", marginRight: "0.5em" }}
            />
            <Input
                value={props.value}
                onChange={e => props.setValue(+e.currentTarget.value)}
                disabled={props.isDisabled}
                inputProps={{
                    step: props.fieldIncrement,
                    min: props.minValue,
                    max: props.maxValue,
                    type: 'number'
                }}
                style={{ alignSelf: "center", marginRight: "0.5em", marginLeft: "0.5em" }}
            />
            <IconButton aria-label="clear" color="secondary" onClick={props.removeValue} disabled={!props.canClear}>
                <ClearIcon />
            </IconButton>
        </div>
    </div>
);

const TextualField = (props: ITextFieldProps) => (
    <div>
        <Typography style={{ fontSize: 14, padding: "0.5em"}}>
            {props.label}
        </Typography>
        <div style={{ display: "flex" }}>
            <Input
                value={props.value ?? ""}
                onChange={e => props.setValue(e.currentTarget.value)}
                disabled={props.isDisabled}
                inputProps={{
                    type: "text",
                }}
                style={{ alignSelf: "center", marginRight: "0.5em", marginLeft: "0.5em" }}
            />
            <IconButton aria-label="clear" color="secondary" onClick={props.removeValue} disabled={!props.canClear}>
                <ClearIcon />
            </IconButton>
        </div>
    </div>
);

class NumericFieldSet extends React.Component<INumericFieldSetProps,INumericFieldSetState> {
    constructor(props) {
        super(props);
        this.state = {
            value: 0,
            isOpen: false,
        };

        this.combinedSetter = value => {
            this.props.setValue(value);
            this.setState(state => ({
                value,
            }));
        }

        this.combinedRemover = () => {
            this.props.removeValue();
            this.setState(state => ({
                value: 0
            }));
        }
    }

    private combinedSetter: (value: number) => void;
    private combinedRemover: () => void;

    public render() {
        const uniformFieldProps = ({
            ...this.props,
            ...createNumericFieldTuple("All", this.state.value, this.combinedSetter, this.combinedRemover),
        });

        return (
            <ExpansionPanel>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography style={{ fontSize: 16 }}>
                        {this.props.header}
                    </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <Grid container direction="column">
                        <Grid item>
                            <NumericField { ...uniformFieldProps } canClear={true} />
                        </Grid>
                        {
                            this.props.fieldSet.map(field => (
                                <Grid item>
                                    <NumericField { ...this.props } { ...field } canClear={this.props.canClear} />
                                </Grid>
                            ))
                        }
                    </Grid>
                </ExpansionPanelDetails>
            </ExpansionPanel>
        );
    }
}

class StylingWidget extends React.Component<IStylingProps, IStylingState> {
    public render() {
        const {
            node,
            isDisabled,
            maxHeight,
            dimensionCommands,
            borderCommands,
            paddingCommands,
            backgroundColorCommands,
            alignmentCommands,
            classNameCommands,
        } = this.props;

        if (node === undefined) {
            return <div></div>
        }

        const getNumericHandler = (setter: (node: DynamoNode, value: number) => void) => (v: number) => setter(node, v);
        const getTextHandler = (setter: (node: DynamoNode, value: string | undefined) => void) => (v: string | undefined) => setter(node, v);
        const getAction = (setter: (node: DynamoNode) => void) => () => setter(node);

        const style: IStyle = node.style;

        const commonProps = ({
            isDisabled,
            minValue: 0,
            maxValue: 25,
            sliderIncrement: 0.1,
            fieldIncrement: 0.5,
            canClear: true,
        });
        const commonColorProps = ({
            isDisabled,
            minValue: 0,
            maxValue: 255,
            sliderIncrement: 1,
            fieldIncrement: 1,
            canClear: false,
        })
        const dimSetProps: INumericFieldSetProps = ({
            ...commonProps,
            header: "Size",
            setValue: getNumericHandler(dimensionCommands.set),
            removeValue: getAction(dimensionCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Width", getValueOrDefault(style.dimensions.width, 0), getNumericHandler(dimensionCommands.setWidth), getAction(dimensionCommands.removeWidth)),
                createNumericFieldTuple("Height", getValueOrDefault(style.dimensions.height, 0), getNumericHandler(dimensionCommands.setHeight), getAction(dimensionCommands.removeHeight)),
            ],
        });
        const borderRadiusSetProps: INumericFieldSetProps = ({
            ...commonProps,
            header: "Border Rounding",
            setValue: getNumericHandler(borderCommands.radiusCommands.set),
            removeValue: getAction(borderCommands.radiusCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Top-Left", getValueOrDefault(style.border.radii.topLeft, 0), getNumericHandler(borderCommands.radiusCommands.setTopLeft), getAction(borderCommands.radiusCommands.removeTopLeft)),
                createNumericFieldTuple("Top-Right", getValueOrDefault(style.border.radii.topRight, 0), getNumericHandler(borderCommands.radiusCommands.setTopRight), getAction(borderCommands.radiusCommands.removeTopRight)),
                createNumericFieldTuple("Botttom-Right", getValueOrDefault(style.border.radii.bottomRight, 0), getNumericHandler(borderCommands.radiusCommands.setBottomRight), getAction(borderCommands.radiusCommands.removeBottomRight)),
                createNumericFieldTuple("Botttom-Left", getValueOrDefault(style.border.radii.bottomLeft, 0), getNumericHandler(borderCommands.radiusCommands.setBottomLeft), getAction(borderCommands.radiusCommands.removeBottomLeft)),
            ]
        });
        const paddingSetProps: INumericFieldSetProps = ({
            ...commonProps,
            header: "Padding",
            setValue: getNumericHandler(paddingCommands.set),
            removeValue: getAction(paddingCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Left", getValueOrDefault(style.padding.left, 0), getNumericHandler(paddingCommands.setLeft), getAction(paddingCommands.removeLeft)),
                createNumericFieldTuple("Top", getValueOrDefault(style.padding.top, 0), getNumericHandler(paddingCommands.setTop), getAction(paddingCommands.removeTop)),
                createNumericFieldTuple("Right", getValueOrDefault(style.padding.right, 0), getNumericHandler(paddingCommands.setRight), getAction(paddingCommands.removeRight)),
                createNumericFieldTuple("Bottom", getValueOrDefault(style.padding.bottom, 0), getNumericHandler(paddingCommands.setBottom), getAction(paddingCommands.removeBottom)),
            ]
        });
        const borderWidthSetProps: INumericFieldSetProps = ({
            ...commonProps,
            header: "Border Width",
            setValue: getNumericHandler(borderCommands.widthCommands.set),
            removeValue: getAction(borderCommands.widthCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Left", getValueOrDefault(style.border.width.left, 0), getNumericHandler(borderCommands.widthCommands.setLeft), getAction(borderCommands.widthCommands.removeLeft)),
                createNumericFieldTuple("Top", getValueOrDefault(style.border.width.top, 0), getNumericHandler(borderCommands.widthCommands.setTop), getAction(borderCommands.widthCommands.removeTop)),
                createNumericFieldTuple("Right", getValueOrDefault(style.border.width.right, 0), getNumericHandler(borderCommands.widthCommands.setRight), getAction(borderCommands.widthCommands.removeRight)),
                createNumericFieldTuple("Bottom", getValueOrDefault(style.border.width.bottom, 0), getNumericHandler(borderCommands.widthCommands.setBottom), getAction(borderCommands.widthCommands.removeBottom)),
            ]
        });
        const borderColorSetProps: INumericFieldSetProps = ({
            ...commonColorProps,
            header: "Border Color",
            setValue: getNumericHandler(borderCommands.colorCommands.set),
            removeValue: getAction(borderCommands.colorCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Red", style.border.color !== undefined ? style.border.color.red : 0, getNumericHandler(borderCommands.colorCommands.setRed)),
                createNumericFieldTuple("Green", style.border.color !== undefined ? style.border.color.green : 0, getNumericHandler(borderCommands.colorCommands.setGreen)),
                createNumericFieldTuple("Blue", style.border.color !== undefined ? style.border.color.blue : 0, getNumericHandler(borderCommands.colorCommands.setBlue)),
            ]
        });
        const backgroundColorSetProps: INumericFieldSetProps = ({
            ...commonColorProps,
            header: "Background Color",
            setValue: getNumericHandler(backgroundColorCommands.set),
            removeValue: getAction(backgroundColorCommands.remove),
            fieldSet: [
                createNumericFieldTuple("Red", style.backgroundColor !== undefined ? style.backgroundColor.red : 0, getNumericHandler(backgroundColorCommands.setRed)),
                createNumericFieldTuple("Green", style.backgroundColor !== undefined ? style.backgroundColor.green : 0, getNumericHandler(backgroundColorCommands.setGreen)),
                createNumericFieldTuple("Blue", style.backgroundColor !== undefined ? style.backgroundColor.blue : 0, getNumericHandler(backgroundColorCommands.setBlue)),
            ]
        });
        const alignmentFieldProps: IAlignmentFieldProps = ({
            alignment: style.horizontalAlignment,
            setValue: (value: string) => alignmentCommands.setHorizontal(node, value),
            removeValue: () => alignmentCommands.remove(node),
        });
        const classNameProps: ITextFieldProps = ({
            ...createTextFieldTuple("Class", style.className, getTextHandler(classNameCommands.set), getAction(classNameCommands.remove)),
            isDisabled: false,
            canClear: true,
        });

        return (
            <div style={{ maxHeight: maxHeight !== undefined ? `${maxHeight}em` : undefined }}>
                <AlignmentField { ...alignmentFieldProps } />
                <TextualField { ...classNameProps} />
                <NumericFieldSet { ...dimSetProps } />
                <NumericFieldSet { ...borderWidthSetProps } />
                <NumericFieldSet { ...borderRadiusSetProps } />
                <NumericFieldSet { ...borderColorSetProps } />
                <NumericFieldSet { ...paddingSetProps } />
                <NumericFieldSet { ...backgroundColorSetProps } />
            </div>
        );
    }
}

const getMaybe = (val: any | undefined, getter) => {
    if (val === undefined || val === null) {
        return undefined;
    }

    return getter(val);
}

const getCopyMaybe = val => getMaybe(val, v => ({ ...v }));

const applyStyleDelta = (currStyle: IStyle | undefined, styleDelta: (style: IStyle) => void): IStyle => ((newStyle: IStyle): IStyle => {
    styleDelta(newStyle);
    return newStyle;
})(currStyle !== undefined ? ({
    dimensions: getCopyMaybe(currStyle.dimensions),
    border: ({
        width: getCopyMaybe(currStyle.border.width),
        radii: getCopyMaybe(currStyle.border.radii),
        color: getCopyMaybe(currStyle.border.color),
    }),
    padding: getCopyMaybe(currStyle.padding),
    horizontalAlignment: currStyle.horizontalAlignment,
    backgroundColor: getCopyMaybe(currStyle.backgroundColor),
    className: currStyle.className,
}) : ({
    dimensions: {},
    border: ({
        width: {},
        radii: {},
    }),
    padding: {},
}))

const stateToProps = (state: IEditorState) => ({
    node: state.selectedNode,
    isDisabled: state.selectedNode === undefined,
    container: state.container
});

const dispatchToProps = dispatch => ((setStyle): IStylingCommandsProps => ({
    dimensionCommands: ((setDim): IDimensionCommands => ({
        set: (node: DynamoNode, size?: number) => setDim(node, size, size),
        setWidth: (node: DynamoNode, width?: number) => setDim(node, width, null),
        setHeight: (node: DynamoNode, height?: number) => setDim(node, null, height),
        remove: (node: DynamoNode) => setDim(node),
        removeWidth: (node: DynamoNode) => setDim(node, undefined, null),
        removeHeight: (node: DynamoNode) => setDim(node, null, undefined)
    }))((node: DynamoNode, width?: number | null, height?: number | null): void => setStyle(node, style => {
        if (width !== null) {
            style.dimensions.width = width;
        }
        if (height !== null) {
            style.dimensions.height = height;
        }
    })),

    borderCommands: ({
        widthCommands: ((setWidth): IBorderWidthCommands => ({
            set: (node: DynamoNode, size?: number) => setWidth(node, size, size, size, size),
            setLeft: (node: DynamoNode, width?: number) => setWidth(node, width, null, null, null),
            setTop: (node: DynamoNode, width?: number) => setWidth(node, null, width, null, null),
            setRight: (node: DynamoNode, width?: number) => setWidth(node, null, null, width, null),
            setBottom: (node: DynamoNode, width?: number) => setWidth(node, null, null, null, width),
            remove: (node: DynamoNode) => setWidth(node),
            removeLeft: (node: DynamoNode) => setWidth(node, undefined, null, null, null),
            removeTop: (node: DynamoNode) => setWidth(node, null, undefined, null, null),
            removeRight: (node: DynamoNode) => setWidth(node, null, null, undefined, null),
            removeBottom: (node: DynamoNode) => setWidth(node, null, null, null, undefined),
        }))((node: DynamoNode, left?: number | null, top?: number | null, right?: number | null, bottom?: number | null): void => setStyle(node, style => {
            const borderWidth = style.border.width;
            if (left !== null) {
                borderWidth.left = left;
            }
            if (top !== null) {
                borderWidth.top = top;
            }
            if (right !== null) {
                borderWidth.right = right;
            }
            if (bottom !== null) {
                borderWidth.bottom = bottom;
            }
        })),
        radiusCommands: ((setRadius): IBorderRadiusCommands => ({
            set: (node: DynamoNode, radius?: number) => setRadius(node, radius, radius, radius, radius),
            setTopLeft: (node: DynamoNode, radius?: number) => setRadius(node, radius, null, null, null),
            setTopRight: (node: DynamoNode, radius?: number) => setRadius(node, null, radius, null, null),
            setBottomRight: (node: DynamoNode, radius?: number) => setRadius(node, null, null, radius, null),
            setBottomLeft: (node: DynamoNode, radius?: number) => setRadius(node, null, null, null, radius),
            remove: (node: DynamoNode) => setRadius(node),
            removeTopLeft: (node: DynamoNode) => setRadius(node, undefined, null, null, null),
            removeTopRight: (node: DynamoNode) => setRadius(node, null, undefined, null, null),
            removeBottomRight: (node: DynamoNode) => setRadius(node, null, null, undefined, null),
            removeBottomLeft: (node: DynamoNode) => setRadius(node, null, null, null, undefined),
        }))((node: DynamoNode, topLeft?: number | null, topRight?: number | null, bottomRight?: number | null, bottomLeft?: number | null): void => setStyle(node, style => {
            const radii = style.border.radii;
            if (topLeft !== null) {
                radii.topLeft = topLeft;
            }
            if (topRight !== null) {
                radii.topRight = topRight;
            }
            if (bottomRight !== null) {
                radii.bottomRight = bottomRight;
            }
            if (bottomLeft !== null) {
                radii.bottomLeft = bottomLeft;
            }
        })),
        colorCommands: ((setColor): IColorCommands => ({
            set: (node: DynamoNode, value?: number) => setColor(node, value, value, value),
            setRed: (node: DynamoNode, value?: number) => setColor(node, value, undefined, undefined),
            setGreen: (node: DynamoNode, value?: number) => setColor(node, undefined, value, undefined),
            setBlue: (node: DynamoNode, value?: number) => setColor(node, undefined, undefined, value),
            remove: (node: DynamoNode) => setColor(node),
        }))((node: DynamoNode, red?: number, green?: number, blue?: number): void => setStyle(node, style => {
            const border = style.border;
            if (border.color === undefined) {
                border.color = Color.getWhite();
            }
            if (red !== undefined) {
                border.color = border.color.withRed(red);
            }
            if (green !== undefined) {
                border.color = border.color.withGreen(green);
            }
            if (blue !== undefined) {
                border.color = border.color.withBlue(blue);
            }
            if (red === undefined && green === undefined && blue === undefined) {
                border.color = undefined;
            }
        })),
    }),
    paddingCommands: ((setPadding): IPaddingCommands => ({
        set: (node: DynamoNode, padding?: number) => setPadding(node, padding, padding, padding, padding),
        setLeft: (node: DynamoNode, padding?: number) => setPadding(node, padding, null, null, null),
        setTop: (node: DynamoNode, padding?: number) => setPadding(node, null, padding, null, null),
        setRight: (node: DynamoNode, padding?: number) => setPadding(node, null, null, padding, null),
        setBottom: (node: DynamoNode, padding?: number) => setPadding(node, null, null, null, padding),
        remove: (node: DynamoNode) => setPadding(node),
        removeLeft: (node: DynamoNode) => setPadding(node, undefined, null, null, null),
        removeTop: (node: DynamoNode) => setPadding(node, null, undefined, null, null),
        removeRight: (node: DynamoNode) => setPadding(node, null, null, undefined, null),
        removeBottom: (node: DynamoNode) => setPadding(node, null, null, null, undefined),
    }))((node: DynamoNode, left?: number | null, top?: number | null, right?: number | null, bottom?: number | null): void => setStyle(node, style => {
        const padding = style.padding;
        if (left !== null) {
            padding.left = left;
        }
        if (top !== null) {
            padding.top = top;
        }
        if (right !== null) {
            padding.right = right;
        }
        if (bottom !== null) {
            padding.bottom = bottom;
        }
    })),

    alignmentCommands: ({
        setHorizontal: (node: DynamoNode, align?: string) => setStyle(node, style => {
            style.horizontalAlignment = align;
        }),
        remove: (node: DynamoNode) => setStyle(node, style => {
            style.horizontalAlignment = undefined;
        }),
    }),
    
    backgroundColorCommands: ((setColor): IColorCommands => ({
        set: (node: DynamoNode, value?: number) => setColor(node, value, value, value),
        setRed: (node: DynamoNode, value?: number) => setColor(node, value, undefined, undefined),
        setGreen: (node: DynamoNode, value?: number) => setColor(node, undefined, value, undefined),
        setBlue: (node: DynamoNode, value?: number) => setColor(node, undefined, undefined, value),
        remove: (node: DynamoNode) => setColor(node),
    }))((node: DynamoNode, red?: number, green?: number, blue?: number): void => setStyle(node, style => {
        if (style.backgroundColor === undefined) {
            style.backgroundColor = Color.getWhite();
        }
        if (red !== undefined) {
            style.backgroundColor = style.backgroundColor.withRed(red);
        }
        if (green !== undefined) {
            style.backgroundColor = style.backgroundColor.withGreen(green);
        }
        if (blue !== undefined) {
            style.backgroundColor = style.backgroundColor.withBlue(blue);
        }
        if (red === undefined && green === undefined && blue === undefined) {
            style.backgroundColor = undefined;
        }
    })),

    classNameCommands: ({
        set: (node: DynamoNode, name?: string) => setStyle(node, style => {
            style.className = name;
        }),
        remove: (node: DynamoNode) => setStyle(node, style => {
            style.className = undefined;
        }),
    })
}))((node: DynamoNode, alterStyle: (style: IStyle) => void) => {
    const curr = node.style;

    dispatch(doSetAttribute(() => {
        node.style = ({
            ...curr,
            ...applyStyleDelta(node.style, alterStyle),
        }) as IStyle;
    },
    () => {
        node.style = curr;
    }));
});

export const EditorStylingWidget = connect(stateToProps, dispatchToProps)(StylingWidget);
